/**
 * ROI-435 (CASA 2.2.2): a password change, a password reset or "Sign out other devices" ends every other
 * session of the user. ROI-BE answers ChangePassword and SignOutOtherDevices with a fresh token for THIS
 * device; the old one no longer works anywhere, including here.
 *
 * The token lives in two places: the region cookie (read first, raw) and localStorage (JSON-quoted). Both are
 * written through their own helpers. A quoted value in the cookie fails isTokenWellFormed and signs the user
 * out, and clearCookieAuth() would also wipe localStorage, so never clear before storing.
 */
import jwtDecode from 'jwt-decode';
import { initializeCookieAuth, setCookieAuth } from './CookieAuth';
import { loadToken, saveToken } from './LocalStorage';

/** The token this tab would send now: the region cookie first, then localStorage (as AxiosInstance does). */
export function currentToken() {
  return initializeCookieAuth() || loadToken() || null;
}

/** The claims of the current token, or {} when there is none or it cannot be decoded. */
export function currentTokenClaims() {
  const token = currentToken();
  if (!token) return {};
  try {
    return jwtDecode(token) || {};
  } catch {
    return {};
  }
}

/**
 * The new token in a ChangePassword / SignOutOtherDevices response. ROI-BE returns
 * { message, isError, result: { token } } on /Users routes (not wrapped by AutoWrapper); the bare
 * { token } shape is accepted too.
 */
export function tokenFromResponse(response) {
  return response?.data?.result?.token ?? response?.data?.token ?? null;
}

/**
 * Replaces this device's token in BOTH stores. Returns false (and changes nothing) when there is no token,
 * e.g. an older ROI-BE that still answers ChangePassword with an empty 200.
 */
export function storeSessionToken(token) {
  if (!token || typeof token !== 'string') return false;
  saveToken(token);
  setCookieAuth(token);
  return true;
}

/**
 * Whether a failed request (401, or a 400 from /Users) is about the session this tab holds NOW.
 *
 * After a password change the new token is stored, but requests already in flight (pollers, other tabs)
 * still carry the old one and come back 401. Clearing on those would wipe the new token and sign every tab
 * out. So clear only when the request carried the current token, or when there is no current token at all.
 *
 * @param {string|undefined} sentToken the token the failed request carried ('' or undefined if none)
 */
export function isFailureForCurrentSession(sentToken) {
  const current = currentToken();
  return !current || sentToken === current;
}
